// src/pages/EditCita.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosInstance';
import { toast } from 'react-toastify';
import {
  Box, TextField, Button, Select, MenuItem, FormControl, InputLabel,
  Typography, Paper, Grid, CircularProgress, Fade, Avatar, Divider,
  Chip, Tooltip, InputAdornment
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import EventIcon from '@mui/icons-material/Event';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SubjectIcon from '@mui/icons-material/Subject';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PaymentsIcon from '@mui/icons-material/Payments';
import PendingActionsIcon from '@mui/icons-material/PendingActions';


// Helper para detectar cancelaciones (AbortController / StrictMode)
const isCancel = (e) =>
  e?.name === 'CanceledError' ||
  e?.name === 'AbortError' ||
  e?.code === 'ERR_CANCELED' ||
  e?.message === 'canceled';

const estadoChipColor = (status) => {
  switch (status) {
    case 'Programada': return 'info';
    case 'Confirmada': return 'secondary';
    case 'Cancelada': return 'error';
    case 'Completada': return 'success';
    default: return 'default';
  }
};

const pagoChip = (estado_pago) => {
  if ((estado_pago || '').toLowerCase() === 'pagado') {
    return {
      label: 'Pagado (PayPal)',
      color: 'success',
      icon: <PaymentsIcon sx={{ fontSize: 18 }} />
    };
  }
  return {
    label: 'Pago en consultorio',
    color: 'warning',
    icon: <PendingActionsIcon sx={{ fontSize: 18 }} />
  };
};

const EditCita = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [citaData, setCitaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [pacienteNombre, setPacienteNombre] = useState('');
  const [medicoNombre, setMedicoNombre] = useState('');

  useEffect(() => {
    const ctrl = new AbortController();

    const fetchData = async () => {
      if (!id || id === 'undefined') {
        console.error("ID de cita inválido");
        setLoading(false);
        toast.error("ID de cita no válido");
        navigate(-1);
        return;
      }
      setLoading(true);
      try {
        // Obtener datos de la cita
        const response = await axiosInstance.get(`/citas/${id}/`, { signal: ctrl.signal });
        const data = response.data;

        setCitaData({
          ...data,
          // Normaliza modalidad por si no viene
          modalidad: data.modalidad || 'Presencial',
          fecha_hora_cita: data.fecha_hora_cita ? new Date(data.fecha_hora_cita) : null,
        });

        // Resolver nombres
        let nombrePaciente = '';
        let nombreMedico = '';
        let generoMedico = '';

        // Paciente
        if (data.paciente_nombre) {
          nombrePaciente = data.paciente_nombre;
        } else if (data.paciente) {
          try {
            const pacienteResponse = await axiosInstance.get(`/pacientes/${data.paciente}/`, { signal: ctrl.signal });
            if (pacienteResponse.data) {
              nombrePaciente = `${pacienteResponse.data.nombres} ${pacienteResponse.data.apellidos}`;
            }
          } catch (error) {
            if (!isCancel(error)) console.error("Error al obtener datos del paciente:", error);
          }
        }

        // Médico
        if (data.medico_nombre) {
          if (data.medico_nombre.startsWith('Dr.') || data.medico_nombre.startsWith('Dra.')) {
            nombreMedico = data.medico_nombre;
          } else {
            const nombreCompleto = data.medico_nombre;
            if (data.medico_genero) {
              generoMedico = data.medico_genero;
            } else {
              const nombrePila = nombreCompleto.split(' ')[0].toLowerCase();
              const nombresFemeninos = ['maría', 'ana', 'laura', 'carmen', 'sofia', 'isabel', 'patricia', 'elena'];
              generoMedico = nombresFemeninos.includes(nombrePila) ? 'F' : 'M';
            }
            const prefijo = generoMedico === 'F' ? 'Dra.' : 'Dr.';
            nombreMedico = `${prefijo} ${nombreCompleto}`;
          }
        } else if (data.medico) {
          try {
            const medicoResponse = await axiosInstance.get(`/medicos/${data.medico}/`, { signal: ctrl.signal });
            if (medicoResponse.data && medicoResponse.data.usuario) {
              const nombre = `${medicoResponse.data.usuario.first_name} ${medicoResponse.data.usuario.last_name}`;
              if (medicoResponse.data.genero) {
                generoMedico = medicoResponse.data.genero;
              } else {
                const nombrePila = medicoResponse.data.usuario.first_name.toLowerCase();
                const nombresFemeninos = ['maría', 'ana', 'laura', 'carmen', 'sofia', 'isabel', 'patricia', 'elena'];
                generoMedico = nombresFemeninos.includes(nombrePila) ? 'F' : 'M';
              }
              const prefijo = generoMedico === 'F' ? 'Dra.' : 'Dr.';
              nombreMedico = `${prefijo} ${nombre}`;
              if (medicoResponse.data.especialidad_principal) {
                nombreMedico += ` - ${medicoResponse.data.especialidad_principal}`;
              }
            }
          } catch (error) {
            if (!isCancel(error)) console.error("Error al obtener datos del médico:", error);
          }
        }

        setPacienteNombre(nombrePaciente || 'No disponible');
        setMedicoNombre(nombreMedico || 'No disponible');

        setLoading(false);
        setTimeout(() => setFadeIn(true), 100);
      } catch (error) {
        if (isCancel(error)) return;
        console.error("Error al cargar los datos de la cita:", error);
        toast.error("No se pudieron cargar los datos para la edición.");
        setLoading(false);
      }
    };

    fetchData();
    return () => ctrl.abort();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCitaData(prevState => ({ ...prevState, [name]: value }));
  };

  const handleDateChange = (newValue) => {
    setCitaData(prevState => ({ ...prevState, fecha_hora_cita: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      paciente: citaData.paciente,
      medico: citaData.medico,
      fecha_hora_cita: citaData.fecha_hora_cita ? citaData.fecha_hora_cita.toISOString() : null,
      motivo_cita: citaData.motivo_cita,
      estado_cita: citaData.estado_cita,
      duracion_minutos: citaData.duracion_minutos,
      modalidad: citaData.modalidad || 'Presencial', // NUEVO: enviar modalidad
    };

    try {
      await axiosInstance.put(`/citas/${id}/`, payload);
      toast.success('¡Cita actualizada con éxito!');
      navigate(-1);
    } catch (error) {
      console.error('Error al actualizar la cita:', error.response?.data || error);
      toast.error('Hubo un error al actualizar la cita.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !citaData) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          Cargando datos de la cita...
        </Typography>
      </Box>
    );
  }

  // Chips vistosos para Estado, Modalidad y Pago
  const pagoInfo = pagoChip(citaData.estado_pago);

  return (
    <Box p={4} sx={{ maxWidth: '1200px', margin: '0 auto' }}>
      <Button
        variant="outlined"
        onClick={() => navigate(-1)}
        startIcon={<ArrowBackIcon />}
        sx={{
          mb: 4,
          borderRadius: '12px',
          transition: 'all 0.2s ease',
          textTransform: 'none',
          '&:hover': { transform: 'translateX(-5px)' }
        }}
      >
        Volver a Citas
      </Button>

      <Fade in={fadeIn} timeout={800}>
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 4,
            borderRadius: '16px',
            boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          {/* Encabezado vistoso con chips de estado, modalidad y pago */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{
                bgcolor: 'primary.main',
                width: 56,
                height: 56,
                mr: 2,
                boxShadow: '0 6px 14px rgba(0,0,0,0.15)'
              }}>
                <EventIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5, letterSpacing: -0.25 }}>
                  Editar Cita
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                  <Chip
                    label={citaData.estado_cita}
                    color={estadoChipColor(citaData.estado_cita)}
                    sx={{ fontWeight: 700 }}
                    variant="filled"
                  />
                  <Chip
                    icon={
                      (citaData.modalidad || 'Presencial') === 'Telemedicina'
                        ? <VideocamIcon sx={{ fontSize: 18 }} />
                        : <LocationOnIcon sx={{ fontSize: 18 }} />
                    }
                    label={citaData.modalidad || 'Presencial'}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 700 }}
                  />
                  <Chip
                    icon={pagoInfo.icon}
                    label={pagoInfo.label}
                    color={pagoInfo.color}
                    variant="outlined"
                    sx={{ fontWeight: 700 }}
                  />
                </Box>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary">
              ID: {id}
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Paciente
                  </Typography>
                </Box>
                <TextField
                  label="Paciente"
                  value={pacienteNombre}
                  disabled
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocalHospitalIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Médico
                  </Typography>
                </Box>
                <TextField
                  label="Médico"
                  value={medicoNombre}
                  disabled
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              {/* Fecha y Hora */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Fecha y Hora
                  </Typography>
                </Box>
                <DateTimePicker
                  label="Seleccionar Fecha y Hora"
                  value={citaData.fecha_hora_cita}
                  onChange={handleDateChange}
                  slotProps={{
                    textField: {
                      required: true,
                      fullWidth: true,
                      sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px' } }
                    },
                    layout: {
                      sx: {
                        '.MuiPickersLayout-contentWrapper': {
                          overflowY: 'auto',
                          scrollbarWidth: 'none',
                          '&::-webkit-scrollbar': { display: 'none' },
                          msOverflowStyle: 'none',
                        },
                        '.MuiClock-container': {
                          overflowY: 'auto',
                          scrollbarWidth: 'none',
                          '&::-webkit-scrollbar': { display: 'none' },
                          msOverflowStyle: 'none',
                        }
                      }
                    }
                  }}
                />
              </Grid>

              {/* Duración */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Duración
                  </Typography>
                </Box>
                <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                  <InputLabel>Modalidad</InputLabel>
                  <Select
                    name="modalidad"
                    value={citaData.modalidad || 'Presencial'}
                    label="Modalidad"
                    onChange={handleChange}
                    sx={{ borderRadius: '12px' }}
                    renderValue={(value) => (
                      <Chip
                        icon={value === 'Telemedicina'
                          ? <VideocamIcon sx={{ fontSize: 18 }} />
                          : <LocationOnIcon sx={{ fontSize: 18 }} />
                        }
                        label={value}
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                  >
                    <MenuItem value="Presencial">
                      <LocationOnIcon fontSize="small" style={{ marginRight: 8 }} />
                      Presencial
                    </MenuItem>
                    <MenuItem value="Telemedicina">
                      <VideocamIcon fontSize="small" style={{ marginRight: 8 }} />
                      Telemedicina
                    </MenuItem>
                  </Select>
                  {citaData.modalidad === 'Telemedicina' && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      El enlace se enviará antes de la consulta o al iniciar la sesión.
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* MODALIDAD - Editable */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {(citaData.modalidad || 'Presencial') === 'Telemedicina'
                    ? <VideocamIcon sx={{ mr: 1, color: 'primary.main' }} />
                    : <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
                  }
                  <Typography variant="subtitle1" fontWeight="medium">
                    Modalidad
                  </Typography>
                </Box>
                <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                  <InputLabel>Modalidad</InputLabel>
                  <Select
                    name="modalidad"
                    value={citaData.modalidad || 'Presencial'}
                    label="Modalidad"
                    onChange={handleChange}
                    sx={{ borderRadius: '12px' }}
                    startAdornment={
                      <InputAdornment position="start">
                        {(citaData.modalidad || 'Presencial') === 'Telemedicina'
                          ? <VideocamIcon sx={{ color: 'text.secondary' }} />
                          : <LocationOnIcon sx={{ color: 'text.secondary' }} />
                        }
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="Presencial">
                      <LocationOnIcon fontSize="small" style={{ marginRight: 8 }} />
                      Presencial
                    </MenuItem>
                    <MenuItem value="Telemedicina">
                      <VideocamIcon fontSize="small" style={{ marginRight: 8 }} />
                      Telemedicina
                    </MenuItem>
                  </Select>
                  {citaData.modalidad === 'Telemedicina' && (
                    <Tooltip title="Esta cita es virtual. El enlace se enviará al paciente antes de la consulta o se generará al iniciar la sesión de telemedicina.">
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        El enlace se enviará antes de la consulta o al iniciar la sesión.
                      </Typography>
                    </Tooltip>
                  )}
                </FormControl>
              </Grid>

              {/* Motivo */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SubjectIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Motivo de la Cita
                  </Typography>
                </Box>
                <TextField
                  label="Describa el motivo"
                  name="motivo_cita"
                  value={citaData.motivo_cita || ''}
                  onChange={handleChange}
                  required
                  fullWidth
                  multiline
                  rows={3}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
            </Grid>

            {/* Acciones */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  borderRadius: '12px',
                  px: 3,
                  py: 1.2,
                  transition: 'all 0.2s ease',
                  textTransform: 'none',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                  }
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving}
                sx={{
                  borderRadius: '12px',
                  px: 3,
                  py: 1.2,
                  boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                  textTransform: 'none',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 26px rgba(0,0,0,0.16)',
                  }
                }}
              >
                {saving ? <CircularProgress size={24} color="inherit" /> : 'Guardar Cambios'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};

export default EditCita;
